
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Icons } from '../ui/Icons';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'react-hot-toast';

interface HelpArticle {
    id: string;
    title: string;
    description: string;
    category: string;
    steps: string[];
    tips?: string[];
    relatedFeatures?: string[];
}

const HELP_ARTICLES: HelpArticle[] = [
    // Getting Started
    {
        id: 'gs-1',
        category: 'Getting Started',
        title: 'Welcome to Hotel Operations Hub',
        description: 'Learn the basics of navigating and using the scheduling reception.',
        steps: [
            'Log in using your provided email and password. Note: The navigation header is hidden until you log in.',
            'Use the top navigation bar to switch between the Scheduler, Staff List, and Settings.',
            'The Scheduler view shows the current week\'s assignments.',
            'Click on any cell in the scheduler to view or edit shift details.'
        ],
        tips: ['You can use the date picker at the top to jump to any specific week.'],
        relatedFeatures: ['Schedule Management', 'Admin Settings']
    },
    {
        id: 'gs-2',
        category: 'Getting Started',
        title: 'Switching Between Dark and Light Mode',
        description: 'Customize your viewing experience based on your preference.',
        steps: [
            'Go to the Settings page from the top navigation.',
            'Find the "Appearance" or "Theme" section.',
            'Select "Light", "Dark", or "Reception" (which follows your device settings).',
            'The changes are applied instantly.'
        ],
        relatedFeatures: ['Admin Settings']
    },
    {
        id: 'gs-3',
        category: 'Getting Started',
        title: 'Staff Portal Overview',
        description: 'A dedicated interface for employees to manage their schedules and requests.',
        steps: [
            'Log in with your staff credentials.',
            'View your personal schedule for the current and upcoming weeks.',
            'Manage your availability and set date-specific overrides.',
            'Submit and track time off or cover requests.',
            'Book shuttle reservations for guests.',
            'Note: Parking management is handled exclusively by administrators.'
        ],
        relatedFeatures: ['Time Off Requests', 'Shuttle Reservation']
    },
    // Staff Management
    {
        id: 'sm-1',
        category: 'Staff Management',
        title: 'Adding a New Staff Member',
        description: 'How to register new employees in the system.',
        steps: [
            'Navigate to the "Staff Management" page.',
            'Click the "Add Staff" button at the top right.',
            'Fill in the name, email, role, and department.',
            'Set their default availability for each day of the week.',
            'Click "Save" to add them to the directory.'
        ],
        tips: ['Make sure to use a unique email address for each staff member.'],
        relatedFeatures: ['Schedule Management']
    },
    {
        id: 'sm-2',
        category: 'Staff Management',
        title: 'Editing Staff Details',
        description: 'Update existing employee information or availability.',
        steps: [
            'Go to the "Staff Management" page.',
            'Find the staff member in the list and click the "Edit" (pencil) icon.',
            'Update the necessary fields in the modal that appears.',
            'Click "Save Changes" to apply the updates.'
        ],
        relatedFeatures: ['Time Off Requests']
    },
    // Schedule Management
    {
        id: 'sch-1',
        category: 'Schedule Management',
        title: 'Creating a Weekly Schedule',
        description: 'Assign shifts to staff members for the upcoming week.',
        steps: [
            'Open the "Scheduler" view.',
            'Select the desired week using the date navigation.',
            'Click on a cell corresponding to a staff member and a specific day.',
            'Choose a shift from the dropdown menu (e.g., Morning, Afternoon, Night).',
            'The reception saves your changes automatically.'
        ],
        tips: ['Use the "Copy Previous Week" feature to save time if schedules are consistent.'],
        relatedFeatures: ['Staff Management', 'Notifications']
    },
    {
        id: 'sch-2',
        category: 'Schedule Management',
        title: 'Printing the Schedule',
        description: 'Generate a physical copy of the weekly roster.',
        steps: [
            'In the Scheduler view, click the "Print" icon at the top right.',
            'A new window will open with a printer-friendly version of the current view.',
            'Use your browser\'s print dialog (Ctrl+P or Cmd+P) to print or save as PDF.'
        ]
    },
    {
        id: 'sch-3',
        category: 'Schedule Management',
        title: 'Advanced Scheduler Features & Metrics',
        description: 'Optimize your staffing using built-in analytics and advanced controls.',
        steps: [
            'View "Total Weekly Hours" at the top of the scheduler to monitor labor costs.',
            'The "Metrics" sidebar provides a breakdown of staffing density by department.',
            'Use "Auto-Archive" (accessible in settings) to keep your schedule list clean.',
            'Toggle "Room Assignments" to overlay housekeeping tasks directly on the schedule.',
            'Enable "Conflict Detection" to get alerted when a staff member is double-booked or exceeds their hour limit.'
        ],
        tips: ['The system automatically flags shifts that conflict with approved time-off requests.'],
        relatedFeatures: ['Staff Management', 'Admin Settings']
    },
    // Shuttle Reservation
    {
        id: 'shu-1',
        category: 'Shuttle Reservation',
        title: 'Booking a Shuttle for Guests',
        description: 'Manage guest transportation to and from the hotel.',
        steps: [
            'Navigate to the "Shuttle" page.',
            'Click "New Reservation".',
            'Select the direction (To Hotel or From Hotel).',
            'Enter the guest name, room number, number of guests, and the pickup location.',
            'Set the date and time for the shuttle.',
            'Click "Confirm Reservation".'
        ],
        tips: ['Always double-check the guest count to ensure the shuttle has enough capacity.'],
        relatedFeatures: ['Notifications']
    },
    {
        id: 'shu-2',
        category: 'Shuttle Reservation',
        title: 'Blocking and Unblocking Shuttle Spots',
        description: 'Manage shuttle capacity by blocking specific time slots or individual seats.',
        steps: [
            'Navigate to the "Shuttle" page.',
            'To block an entire time slot, double-click on the time in the grid.',
            'To block an individual seat, double-click on an empty spot within the "Spot Management" section of a time slot.',
            'Confirm the action in the modal that appears.',
            'To unblock, double-click the blocked spot again and confirm.'
        ],
        tips: ['Blocked spots will show as "BLKD" in the print view.'],
        relatedFeatures: ['Schedule Management']
    },
    // Parking Management
    {
        id: 'prk-1',
        category: 'Parking Management',
        title: 'Managing Parking Reservations and Statuses',
        description: 'Understand the lifecycle of a parking reservation from booking to checkout.',
        steps: [
            'Navigate to the "Parking" page.',
            'New reservations are created with a default status of "Reservation".',
            'Click the "View" (eye) icon on any record to open the preview modal.',
            'In the preview modal, you can update the status to "Check-in" or "Checkout".',
            'Reservations are automatically marked as "Inactive" if they are checked out or if the end date passed more than 2 days ago.'
        ],
        tips: ['Use the "Show Inactive" toggle in the header to filter the list.'],
        relatedFeatures: ['Admin Settings']
    },
    {
        id: 'prk-2',
        category: 'Parking Management',
        title: 'Advanced Parking Features',
        description: 'Learn about sorting, automated archiving, and late checkout notifications.',
        steps: [
            'Click on any column header in the Parking table to sort the list.',
            'Records are automatically archived after 7 days of inactivity (Checked-out or Disactivated).',
            'Look for "Late Checkout" notifications in the bell icon if a guest stays past their scheduled time.',
            'The "Due at Location" field helps track payments that need to be collected on-site.'
        ],
        relatedFeatures: ['Notifications', 'Admin Settings']
    },
    {
        id: 'prk-3',
        category: 'Parking Management',
        title: 'Importing Parking Data',
        description: 'Bulk import reservations using CSV files or direct copy-paste.',
        steps: [
            'Click the "Import" button in the Parking view header.',
            'Select your preferred method: "Upload CSV" or "Copy & Paste".',
            'For Copy & Paste, paste your data directly into the text area.',
            'The system automatically maps fields like Customer Name, License Plate, and Due at Location.',
            'Review the data and click "Import" to complete the process.'
        ],
        tips: ['Ensure your dates are in a standard format (YYYY-MM-DD) for best results.']
    },
    // Time Off Requests
    {
        id: 'to-1',
        category: 'Time Off Requests',
        title: 'Submitting a Time Off Request',
        description: 'How staff can request leave through the portal.',
        steps: [
            'Log in to the Staff Portal.',
            'Click on "Request Time Off".',
            'Select the start and end dates.',
            'Provide a reason for the request.',
            'Submit the form for manager approval.'
        ],
        tips: ['Submit requests at least two weeks in advance for better planning.'],
        relatedFeatures: ['Schedule Management', 'Notifications']
    },
    {
        id: 'to-2',
        category: 'Time Off Requests',
        title: 'Approving or Rejecting Requests',
        description: 'Manager instructions for handling leave applications.',
        steps: [
            'Go to the "Time Off" management page.',
            'Review the list of "Pending" requests.',
            'Click "Approve" or "Reject" on the specific request.',
            'The staff member will receive a notification of the decision.'
        ]
    },
    // Memo & Search Center
    {
        id: 'mc-1',
        category: 'Memo Center',
        title: 'Memo Search and Preview',
        description: 'Quickly find and read hotel policies, memos, and updates.',
        steps: [
            'Navigate to the "Memo Center" from the sidebar or main dashboard.',
            'Use the search bar at the top to search by title or content.',
            'Filter memos by category (e.g., Policy, Event, Urgent) using the tabs.',
            'Click on a memo card to open the "Focus Preview" modal.',
            'Full HTML formatting (tables, images, bold text) is preserved in the preview for easy reading.'
        ],
        tips: ['Use the "Advanced Search" toggle to filter by author or date range.'],
        relatedFeatures: ['Admin Settings']
    },
    {
        id: 'mc-2',
        category: 'Memo Center',
        title: 'Creating and Managing Memos',
        description: 'Publish and organize internal communications for your team.',
        steps: [
            'Managers can navigate to Settings > Memo Management.',
            'Click "Create Memo" to open the rich text editor.',
            'Select a category and topic, then enter your content.',
            'Use the "HTML Source" toggle if you need to paste custom table code or styled snippets.',
            'Click "Publish" to make the memo available to all staff.'
        ],
        relatedFeatures: ['Staff Portal', 'Notifications']
    },
    // Maintenance Module
    {
        id: 'mnt-1',
        category: 'Maintenance',
        title: 'Reporting and Tracking Maintenance Issues',
        description: 'Streamline hotel repairs and facility management.',
        steps: [
            'Go to the "Maintenance" module.',
            'Click "Report Issue" to create a new ticket.',
            'Specify the location (e.g., Room 101, Lobby), category, and priority.',
            'Add photos or a detailed description of the problem.',
            'Track progress from "Pending" to "In Progress" and finally "Resolved".',
            'Staff will be notified when their reported issues are updated.'
        ],
        tips: ['High-priority issues are highlighted in red and appear at the top of the staff dashboard.'],
        relatedFeatures: ['Tasks', 'Notifications']
    },
    {
        id: 'mnt-2',
        category: 'Maintenance',
        title: 'Managing Maintenance Categories',
        description: 'Organize your facility management by defining issue types.',
        steps: [
            'Admins can access Maintenance > Settings.',
            'Add or edit categories like Plumbing, Electrical, or Housekeeping requests.',
            'Assign default response times or SLAs to each category.',
            'View reports on common issues to identify systemic maintenance needs.'
        ]
    },
    // Tasks Module
    {
        id: 'tsk-1',
        category: 'Tasks',
        title: 'Daily Task Management & Steps',
        description: 'Manage daily operational lists and detailed instructional workflows.',
        steps: [
            'Access the "Tasks" module to see your assigned checklist for today.',
            'Click on a task to expand its "Instructional Steps".',
            'Check off items as you complete them; progress is saved in real-time.',
            'Managers can create recurring tasks that automatically reset each day.',
            'Use the "Night Audit" task list for critical end-of-day operations.'
        ],
        tips: ['Tasks with nested steps are ideal for training new staff on complex procedures.'],
        relatedFeatures: ['Maintenance', 'Schedule Management']
    },
    {
        id: 'tsk-2',
        category: 'Tasks',
        title: 'Reordering Task steps & Sub-steps',
        description: 'Customize the sequence of your operational workflows.',
        steps: [
            'Go to Settings > Task Configuration.',
            'Locate the task template you wish to modify.',
            'Use the "Drag Handle" (vertical grip) next to each step or sub-step to reorder it.',
            'The numbering (e.g., 1.1, 1.2) will automatically update based on the new sequence.',
            'Click "Save Template" to apply the new sequence to all future task instances.'
        ],
        tips: ['Logical sequencing helps staff follow procedures more efficiently and reduces errors.'],
        relatedFeatures: ['TaskManager', 'Night Audit']
    },
    // Scheduler Updated Features
    {
        id: 'sch-4',
        category: 'Schedule Management',
        title: 'Scheduler Visibility Improvements',
        description: 'Enhancements to make reading the schedule easier and more intuitive.',
        steps: [
            'The scheduler and all date headers now prominently display the day name (e.g., Monday, Tuesday).',
            'This applies to the desktop scheduler, staff portal view, and printed roster.',
            'Use the department filter to isolate specific teams while planning.',
            'The "Metrics" sidebar updates instantly as you assign shifts to show coverage levels.'
        ],
        relatedFeatures: ['Staff Portal']
    },
    // Attendance Management
    {
        id: 'att-1',
        category: 'Staff Management',
        title: 'Reviewing and Approving Attendance',
        description: 'Ensuring accurate payroll by validating staff shift time updates.',
        steps: [
            'Navigate to the "Attendance" page.',
            'The "Pending Time Updates" table shows all shifts where staff reported different hours than scheduled.',
            'Review the "Reason" provided by the staff member for the discrepancy.',
            'Click the green "Check" icon to approve the update, or the red "X" to reject it.',
            'Approved updates will reflected in the Biweekly Payroll Report.'
        ],
        tips: ['Use the print button to generate a physical record of the attendance period for your files.'],
        relatedFeatures: ['Payroll Reports', 'Staff Portal']
    },
    // Notifications
    {
        id: 'not-1',
        category: 'Notifications',
        title: 'Managing Your Notifications',
        description: 'Stay updated on schedule changes and requests.',
        steps: [
            'Click the "Bell" icon in the top header to open the notification panel.',
            'Unread notifications are highlighted.',
            'Click on a notification to mark it as read or view details.',
            'Use "Mark all as read" to clear the list quickly.'
        ]
    },
    // Admin Settings
    {
        id: 'set-1',
        category: 'Admin Settings',
        title: 'Managing Departments and Shifts',
        description: 'Configure the core structure of your hotel operations.',
        steps: [
            'Navigate to the "Settings" page.',
            'Under "Departments", you can add, edit, or delete hotel areas (e.g., Front Desk, Housekeeping).',
            'Under "Shifts", define the working hours for different time slots.',
            'Changes here will reflect in the Scheduler and Staff Management views.'
        ],
        tips: ['Assigning unique colors to departments helps in visual scheduling.'],
        relatedFeatures: ['Staff Management', 'Schedule Management']
    },
    {
        id: 'acc-1',
        category: 'Access Manager',
        title: 'Managing Shared Credentials',
        description: 'Store and retrieve shared website logins for your team safely.',
        steps: [
            'Navigate to the "Access Manager" module from the main navigation.',
            'Click "Add Credential" to store a new website login.',
            'Enter the website name, URL, username, and password.',
            'Add optional notes for recovery or special instructions.',
            'Click "Save Record" to add it to the shared internal list.'
        ],
        tips: ['Use the copy icons to instantly copy credentials to your clipboard without revealing them.'],
        relatedFeatures: ['Admin Settings']
    },
    {
        id: 'rep-1',
        category: 'Reports',
        title: 'Staff Working Hours & Department Filtering',
        description: 'Generate detailed payroll reports with targeted department selection.',
        steps: [
            'Go to the "Reports" page.',
            'Select "Staff Working Hours Report".',
            'Choose a start date (the period covers two weeks).',
            'Use the "Departments" filter to select one or more specific departments, or choose "All Departments".',
            'Click "Generate Report" to preview the data.',
            'Use the "Print Tool" to generate separate pages for each department.'
        ],
        tips: ['Filtering by specific departments helps in processing payroll for different teams individually.'],
        relatedFeatures: ['Staff Management', 'Attendance Management']
    },
    // Troubleshooting
    {
        id: 'tr-1',
        category: 'Troubleshooting',
        title: 'Common Login Issues',
        description: 'What to do if you cannot access your account.',
        steps: [
            'Ensure your email address is typed correctly without extra spaces.',
            'Check if "Caps Lock" is on while typing your password.',
            'If you forgot your password, click "Forgot Password" on the login screen.',
            'Contact your reception administrator if your account is suspended.'
        ]
    },
    {
        id: 'tr-2',
        category: 'Troubleshooting',
        title: 'Data Not Saving',
        description: 'Steps to take if changes are not persisting.',
        steps: [
            'Check your internet connection.',
            'Refresh the page to ensure you have the latest data.',
            'If you see a "Permission Denied" error, your session may have expired. Try logging out and back in.'
        ]
    },
    {
        id: 'tr-3',
        category: 'Troubleshooting',
        title: 'Resetting the Application',
        description: 'Use the Emergency Reset tool if the app feels broken or data is inconsistent.',
        steps: [
            'On the login screen, look for the "Having issues? Reset App" link at the bottom.',
            'If you are logged in and seeing strange behavior, you can also trigger a reset from the footer of the login screen.',
            'Click "Confirm Reset" in the modal that appears.',
            'This will clear your local cache, log you out, and perform a hard reload.',
            'Warning: This will clear all unsaved drafts and local preferences.'
        ],
        tips: ['Try a simple page refresh first before using the Hard Reset.'],
        relatedFeatures: ['Admin Settings']
    },
    // Specialized Modules
    {
        id: 'spec-1',
        category: 'Admin Settings',
        title: 'Full HTML Memo Editor',
        description: 'Learn how to use the advanced rich text editor for memos and policies.',
        steps: [
            'Navigate to Settings > Memo Management.',
            'When creating or editing a memo, use the "Visual Editor" for standard formatting.',
            'To input raw HTML (e.g., from an email or web snippet), toggle the "Edit HTML Source" button.',
            'The editor now supports tables, nested lists, and custom styling.',
            'Your content is automatically sanitized for safety while preserving formatting.'
        ],
        tips: ['You can paste content directly from Word or Google Docs and formatting will be preserved.'],
        relatedFeatures: ['Memo Management']
    }
];

const CATEGORIES = [
    'All',
    'Getting Started',
    'Staff Management',
    'Schedule Management',
    'Memo Center',
    'Maintenance',
    'Tasks',
    'Shuttle Reservation',
    'Parking Management',
    'Time Off Requests',
    'Reports',
    'Access Manager',
    'Notifications',
    'Admin Settings',
    'Troubleshooting',
    'What\'s New'
];

const WHATS_NEW = [
    {
        date: 'April 2026',
        title: 'Access Manager Module',
        description: 'Store and manage shared website credentials internally. Easily copy usernames and passwords to your clipboard and navigate to sites with one click.',
        icon: 'Key'
    },
    {
        date: 'April 2026',
        title: 'Advanced Report Filtering',
        description: 'You can now select multiple specific departments when generating the Staff Working Hours report, allowing for targeted payroll analysis.',
        icon: 'Filter'
    },
    {
        date: 'May 2026',
        title: 'Task Step Reordering',
        description: 'You can now manually set the order of steps and sub-steps in task configurations, giving you full control over operational workflows.',
        icon: 'Move'
    },
    {
        date: 'May 2026',
        title: 'Day Names in Scheduler',
        description: 'The scheduler and date headers now prominently display day names (e.g., Monday) for improved readability and quicker navigation.',
        icon: 'Calendar'
    },
    {
        date: 'April 2026',
        title: 'Full HTML Memo Support',
        description: 'Turned the memo editor into a true HTML content system. Support for tables, custom styling, and a new Raw HTML source editing mode.',
        icon: 'Code'
    },
    {
        date: 'April 2026',
        title: 'Task Instruction Steps',
        description: 'Daily tasks now support nested, interactive steps. Perfect for complex operational procedures and night audit workflows.',
        icon: 'List'
    },
    {
        date: 'April 2026',
        title: 'Emergency Reset Utility',
        description: 'Added a powerful reset tool to the login screen to help users fix broken local states and clear corrupted caches safely.',
        icon: 'AlertTriangle'
    },
    {
        date: 'March 2026',
        title: 'Scheduler Metrics & Analytics',
        description: 'New real-time hour tracking and departmental density metrics in the scheduler sidebar.',
        icon: 'BarChart'
    },
    {
        date: 'March 2026',
        title: 'Shuttle & Parking Enhancements',
        description: 'Added double-click spot blocking for shuttles, advanced parking sorting, automated archiving, and a flexible data import tool.',
        icon: 'Zap'
    },
    {
        date: 'March 2026',
        title: 'Admin Password Management',
        description: 'Administrators can now reset passwords for other admin users directly from the admin management area.',
        icon: 'Lock'
    },
    {
        date: 'February 2026',
        title: 'Departmental Print Filters',
        description: 'Added the ability to filter the schedule by department when printing.',
        icon: 'Printer'
    },
    {
        date: 'January 2026',
        title: 'Dark Mode Support',
        description: 'Full support for dark mode across the entire application.',
        icon: 'Moon'
    }
];

interface HelpCenterViewProps {
    isMobile?: boolean;
}

const HelpCenterView: React.FC<HelpCenterViewProps> = ({ isMobile = false }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [feedbackStatus, setFeedbackStatus] = useState<{[key: string]: 'up' | 'down' | null}>({});

    const filteredArticles = useMemo(() => {
        if (selectedCategory === 'What\'s New') return [];
        return HELP_ARTICLES.filter(article => {
            const matchesSearch = 
                article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                article.category.toLowerCase().includes(searchQuery.toLowerCase());
            
            const matchesCategory = !selectedCategory || article.category === selectedCategory;
            
            return matchesSearch && matchesCategory;
        });
    }, [searchQuery, selectedCategory]);

    const selectedArticle = useMemo(() => {
        return HELP_ARTICLES.find(a => a.id === selectedArticleId);
    }, [selectedArticleId]);

    const handleBack = () => {
        setSelectedArticleId(null);
    };

    const handleFeedback = (articleId: string, type: 'up' | 'down') => {
        setFeedbackStatus(prev => ({ ...prev, [articleId]: type }));
        toast.success('Thank you for your feedback!');
    };

    const renderWhatsNew = () => (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <Icons.History className="w-6 h-6 text-indigo-600" />
                What's New in Hotel Operations Hub
            </h2>
            <div className="space-y-4">
                {WHATS_NEW.map((item, idx) => {
                    const Icon = (Icons as any)[item.icon] || Icons.Info;
                    return (
                        <div key={idx} className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">
                                    <Icon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <div>
                                    <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">{item.date}</span>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mt-1">{item.title}</h3>
                                    <p className="text-gray-600 dark:text-gray-400 mt-2">{item.description}</p>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const SupportTicketModal = () => {
        const [submitting, setSubmitting] = useState(false);
        const [ticketData, setTicketData] = useState({ subject: '', description: '', priority: 'medium' });

        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            setSubmitting(true);
            // Simulate API call
            setTimeout(() => {
                setSubmitting(false);
                setShowSupportModal(false);
                toast.success('Support ticket submitted successfully! Reference: #' + Math.floor(Math.random() * 100000));
            }, 1500);
        };

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white dark:bg-slate-800 rounded-3xl p-8 w-full max-w-lg shadow-2xl border border-gray-100 dark:border-slate-700"
                >
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Contact Support</h2>
                        <button onClick={() => setShowSupportModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors">
                            <Icons.X className="w-6 h-6 text-gray-500" />
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                            <input 
                                required
                                type="text" 
                                value={ticketData.subject}
                                onChange={e => setTicketData({...ticketData, subject: e.target.value})}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                placeholder="Briefly describe the issue"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                            <select 
                                value={ticketData.priority}
                                onChange={e => setTicketData({...ticketData, priority: e.target.value})}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                            >
                                <option value="low">Low - General Inquiry</option>
                                <option value="medium">Medium - Technical Issue</option>
                                <option value="high">High - Critical Blocker</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                            <textarea 
                                required
                                rows={4}
                                value={ticketData.description}
                                onChange={e => setTicketData({...ticketData, description: e.target.value})}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                placeholder="Please provide more details about your problem..."
                            />
                        </div>
                        <button 
                            disabled={submitting}
                            type="submit" 
                            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {submitting ? <Icons.Loader2 className="w-5 h-5 animate-spin" /> : <Icons.Send className="w-5 h-5" />}
                            Submit Ticket
                        </button>
                    </form>
                </motion.div>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-12 text-center">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">How can we help you?</h1>
                <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                    Search our help center or browse categories below to find answers to your questions.
                </p>
                
                {/* SearchBar */}
                <div className="mt-8 max-w-xl mx-auto relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Icons.Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search for articles, features, or keywords..."
                        className="block w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all dark:text-white"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            <div className={`flex flex-col ${isMobile ? '' : 'lg:flex-row'} gap-8`}>
                {/* Sidebar: CategoryList */}
                <div className={`w-full ${isMobile ? '' : 'lg:w-1/4'} space-y-6`}>
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-700">
                        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Categories</h2>
                        <nav className="space-y-1">
                            <button
                                onClick={() => { setSelectedCategory(null); setSelectedArticleId(null); }}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!selectedCategory ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                            >
                                All Articles
                            </button>
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => { setSelectedCategory(cat); setSelectedArticleId(null); }}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCategory === cat ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* QuickTipsPanel */}
                    <div className="bg-indigo-600 rounded-2xl p-6 text-white shadow-lg">
                        <div className="flex items-center gap-2 mb-4">
                            <Icons.Info className="h-5 w-5" />
                            <h3 className="font-bold">Quick Tips</h3>
                        </div>
                        <ul className="space-y-3 text-sm text-indigo-100">
                            <li className="flex gap-2">
                                <span className="font-bold">•</span>
                                <span>Use the "Search" bar to find specific topics quickly.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-bold">•</span>
                                <span>Dark mode can be toggled in the Settings menu.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-bold">•</span>
                                <span>Check the "Troubleshooting" section for common fixes.</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Main Content: ArticleViewer or ArticleList */}
                <div className={`w-full ${isMobile ? '' : 'lg:w-3/4'}`}>
                    <AnimatePresence mode="wait">
                        {selectedArticleId && selectedArticle ? (
                            <motion.div
                                key="article"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-slate-700"
                            >
                                <button
                                    onClick={handleBack}
                                    className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 font-medium mb-6 hover:underline"
                                >
                                    <Icons.ArrowLeft className="h-4 w-4" />
                                    Back to articles
                                </button>

                                <div className="mb-8">
                                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 mb-4">
                                        {selectedArticle.category}
                                    </span>
                                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{selectedArticle.title}</h2>
                                    <p className="text-lg text-gray-600 dark:text-gray-400">{selectedArticle.description}</p>
                                </div>

                                <div className="space-y-8">
                                    <section>
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white text-xs">1</span>
                                            Step-by-Step Instructions
                                        </h3>
                                        <div className="space-y-4 ml-8">
                                            {selectedArticle.steps.map((step, idx) => (
                                                <div key={idx} className="flex gap-4">
                                                    <span className="text-indigo-600 dark:text-indigo-400 font-bold">{idx + 1}.</span>
                                                    <p className="text-gray-700 dark:text-gray-300">{step}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </section>

                                    {selectedArticle.tips && selectedArticle.tips.length > 0 && (
                                        <section className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-6 rounded-r-xl">
                                            <h4 className="text-amber-800 dark:text-amber-400 font-bold mb-2 flex items-center gap-2">
                                                <Icons.Info className="h-5 w-5" />
                                                Pro Tips
                                            </h4>
                                            <ul className="list-disc list-inside text-amber-700 dark:text-amber-300 space-y-1">
                                                {selectedArticle.tips.map((tip, idx) => (
                                                    <li key={idx}>{tip}</li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}
                                    {selectedArticle.relatedFeatures && (
                                        <section className="pt-8 border-t border-gray-100 dark:border-slate-700">
                                            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Related Features</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedArticle.relatedFeatures.map(feat => (
                                                    <span key={feat} className="px-3 py-1 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm">
                                                        {feat}
                                                    </span>
                                                ))}
                                            </div>
                                        </section>
                                    )}

                                    {/* Article Feedback */}
                                    <section className="pt-8 border-t border-gray-100 dark:border-slate-700 flex flex-col items-center text-center">
                                        <h4 className="text-gray-900 dark:text-white font-bold mb-4">Was this article helpful?</h4>
                                        <div className="flex gap-4">
                                            <button 
                                                onClick={() => handleFeedback(selectedArticle.id, 'up')}
                                                className={`flex items-center gap-2 px-6 py-2 rounded-xl border transition-all ${feedbackStatus[selectedArticle.id] === 'up' ? 'bg-green-50 border-green-200 text-green-600' : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                                            >
                                                <Icons.ThumbsUp className="w-4 h-4" />
                                                Yes
                                            </button>
                                            <button 
                                                onClick={() => handleFeedback(selectedArticle.id, 'down')}
                                                className={`flex items-center gap-2 px-6 py-2 rounded-xl border transition-all ${feedbackStatus[selectedArticle.id] === 'down' ? 'bg-red-50 border-red-200 text-red-600' : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                                            >
                                                <Icons.ThumbsDown className="w-4 h-4" />
                                                No
                                            </button>
                                        </div>
                                    </section>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="list"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="space-y-6"
                            >
                                {filteredArticles.length > 0 ? (
                                    <div className={`grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-2'} gap-4`}>
                                        {filteredArticles.map(article => (
                                            <button
                                                key={article.id}
                                                onClick={() => setSelectedArticleId(article.id)}
                                                className="text-left bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 hover:border-indigo-500 dark:hover:border-indigo-500 transition-all group"
                                            >
                                                <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-2 block">
                                                    {article.category}
                                                </span>
                                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                                    {article.title}
                                                </h3>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                                                    {article.description}
                                                </p>
                                                <div className="mt-4 flex items-center text-sm font-medium text-indigo-600 dark:text-indigo-400">
                                                    Read article
                                                    <Icons.ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                ) : selectedCategory === 'What\'s New' ? (
                                    renderWhatsNew()
                                ) : (
                                    <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-slate-700">
                                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-700 mb-4">
                                            <Icons.Search className="h-8 w-8 text-gray-400" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No articles found</h3>
                                        <p className="text-gray-500 dark:text-gray-400">
                                            We couldn't find any help articles matching "{searchQuery}".
                                            <br />Try using different keywords or browse by category.
                                        </p>
                                        <button
                                            onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}
                                            className="mt-6 text-indigo-600 dark:text-indigo-400 font-semibold hover:underline"
                                        >
                                            Clear all filters
                                        </button>
                                    </div>
                                )}

                                {/* ContactSupportCard */}
                                <div className="mt-12 bg-gray-900 dark:bg-slate-950 rounded-3xl p-8 text-white relative overflow-hidden">
                                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                                        <div className="text-center md:text-left">
                                            <h3 className="text-2xl font-bold mb-2">Still need help?</h3>
                                            <p className="text-gray-400">Our support team is always here to assist you with any technical issues.</p>
                                        </div>
                                        <button 
                                            onClick={() => setShowSupportModal(true)}
                                            className="px-8 py-3 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap"
                                        >
                                            Contact Support
                                        </button>
                                    </div>
                                    {/* Decorative background element */}
                                    <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 bg-indigo-600/20 rounded-full blur-3xl"></div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Modals and Overlays */}
            {showSupportModal && <SupportTicketModal />}
        </div>
    );
};

export default HelpCenterView;
